/**
 * Pre-mount owner branding (REGTA-74).
 *
 * index.html loads `/owner-public-branding.js` as a BLOCKING classic
 * script before the app bundle. That script (generated per owner by
 * `packages/auth-data/src/branding-export.ts`, rewritten per owner by
 * CloudFront in prod, written to `public/` by `db:seed:dev` locally)
 * sets `window.REGTA_OWNER_BRANDING` and the `--owner-primary` CSS var.
 *
 * Because the script is blocking, the global is guaranteed to exist
 * before any React render — consumers read it synchronously and the
 * first paint already shows the owner's chrome. If the script is
 * missing (fresh dev checkout before seeding), we fall back to Regta
 * defaults, matching the previous behavior.
 */

export type OwnerPublicBranding = {
  ownerName: string;
  ownerSlug: string;
  logoOnWhiteUrl: string | null;
  logoOnPrimaryUrl: string | null;
  primaryColor: string | null;
};

declare global {
  interface Window {
    REGTA_OWNER_BRANDING?: Partial<OwnerPublicBranding>;
  }
}

/** Regta default chrome green — the fallback when no owner primary is set. */
export const REGTA_PRIMARY = '#1a3d2b';

/**
 * Fixed accent paired with the owner primary (sidebar active border,
 * Översikt card eyebrows). A neutral that reads on any dark owner
 * primary; promote to a per-owner `brandColors` field if an owner ever
 * needs to repaint it.
 */
export const BRAND_ACCENT = '#B8D4A8';

export const FALLBACK_OWNER_BRANDING: OwnerPublicBranding = {
  ownerName: 'Regta',
  ownerSlug: 'regta',
  logoOnWhiteUrl: null,
  logoOnPrimaryUrl: null,
  primaryColor: REGTA_PRIMARY,
};

export function ownerPublicBranding(): OwnerPublicBranding {
  const injected = typeof window === 'undefined' ? undefined : window.REGTA_OWNER_BRANDING;
  return {
    ownerName: injected?.ownerName ?? FALLBACK_OWNER_BRANDING.ownerName,
    ownerSlug: injected?.ownerSlug ?? FALLBACK_OWNER_BRANDING.ownerSlug,
    logoOnWhiteUrl: injected?.logoOnWhiteUrl ?? null,
    logoOnPrimaryUrl: injected?.logoOnPrimaryUrl ?? null,
    primaryColor: injected?.primaryColor ?? FALLBACK_OWNER_BRANDING.primaryColor,
  };
}
